import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAssistantUnread } from "../../context/AssistantUnreadContext";
import PlatformNotificationToast from "../common/PlatformNotificationToast.jsx";

export default function AssistantNotificationToast() {
  const { latestNotification, clearLatestNotification } = useAssistantUnread();
  const { t } = useTranslation("menu");
  const location = useLocation();
  const navigate = useNavigate();
  const portalRoot =
    typeof document !== "undefined"
      ? document.getElementById("modal-root") || document.body
      : null;

  const isAssistantPage = location.pathname.startsWith("/assistant/");
  const isAuthPage = location.pathname === "/auth";

  useEffect(() => {
    if (isAssistantPage && latestNotification) {
      clearLatestNotification?.();
    }
  }, [clearLatestNotification, isAssistantPage, latestNotification]);

  useEffect(() => {
    if (!latestNotification) return undefined;
    const timer = window.setTimeout(() => {
      clearLatestNotification?.();
    }, 10000);
    return () => window.clearTimeout(timer);
  }, [clearLatestNotification, latestNotification]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        clearLatestNotification?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [clearLatestNotification]);

  // Registration can create and seed the assistant session before AuthPage
  // finishes its welcome animation. Keep that notification queued until the
  // user has actually entered the platform.
  if (!latestNotification || isAssistantPage || isAuthPage) return null;

  const rawPreviewText = String(latestNotification?.text || "").replace(/\s+/g, " ").trim();
  const previewText = rawPreviewText.startsWith("You just opened another person's Vault.")
    ? t("assistant.toast.vault_opened_by_user")
    : rawPreviewText;

  return (
    <PlatformNotificationToast
      badge={t("assistant.toast.badge", "Assistant")}
      title={t("assistant.toast.title", "New message")}
      message={previewText}
      openAriaLabel={t("assistant.toast.open", t("mailbox.toast.open", "OPEN"))}
      onDismiss={() => clearLatestNotification?.()}
      onOpen={() => {
        clearLatestNotification?.();
        navigate("/assistant/core");
      }}
      portalRoot={portalRoot}
    />
  );
}
